import React from 'react';
import { ChakraProvider } from '@chakra-ui/react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider } from './contexts/AuthContext';
import { Layout } from './components/common/Layout';
import { ProtectedRoute } from './components/common/ProtectedRoute';
import { HomeRoute } from './components/common/HomeRoute';

// Pages
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import EventsPage from './pages/EventsPage';
import EventDetailPage from './pages/EventDetailPage';
import CreateEventPage from './pages/CreateEventPage';
import EditEventPage from './pages/EditEventPage';
import ContactsPage from './pages/ContactsPage';
import CRMSyncPage from './pages/CRMSyncPage';
import EmailPalettePage from './pages/EmailPalettePage';
import NotFoundPage from './pages/NotFoundPage';
import RSVPPage from './pages/RSVPPage';
import CheckInPage from './pages/CheckInPage';
import QRScannerPage from './pages/QRScannerPage';
import VIPBoardPage from './pages/VIPBoardPage';
import CheckinDemoPage from './pages/CheckinDemoPage';
import AccountsPage from './pages/AccountsPage';
import ProfilePage from './pages/ProfilePage';

import theme from './theme';

// Create a client for TanStack Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

function App() {
  return (
    <ChakraProvider theme={theme}>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Router>
            <Routes>
              {/* Public routes */}
              <Route path="/login" element={<LoginPage />} />

              {/* RSVP Route - Public, no authentication required */}
              <Route path="/rsvp/:token" element={<RSVPPage />} />

              {/* Protected routes */}
              <Route path="/" element={
                <ProtectedRoute>
                  <HomeRoute>
                    <Layout>
                      <DashboardPage />
                    </Layout>
                  </HomeRoute>
                </ProtectedRoute>
              } />

              {/* Events routes */}
              <Route path="/events" element={
                <ProtectedRoute>
                  <Layout>
                    <EventsPage />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/events/create" element={
                <ProtectedRoute requiredRole="organizer">
                  <Layout>
                    <CreateEventPage />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/events/:id" element={
                <ProtectedRoute>
                  <Layout>
                    <EventDetailPage />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/events/:id/edit" element={
                <ProtectedRoute requiredRole="organizer">
                  <Layout>
                    <EditEventPage />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Contacts routes (admin only) */}
              <Route path="/contacts" element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <ContactsPage />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* CRM Sync routes (admin only) */}
              <Route path="/crm" element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <CRMSyncPage />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Email palette (admin only) */}
              <Route path="/settings/email-palette" element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <EmailPalettePage />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Accounts (admin only) */}
              <Route path="/settings/accounts" element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <AccountsPage />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Mon profil: any signed-in user */}
              <Route path="/profile" element={
                <ProtectedRoute>
                  <Layout>
                    <ProfilePage />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Check-in simulation demo (admin only; the backend 404s in production) */}
              <Route path="/demo/checkin" element={
                <ProtectedRoute requiredRole="admin">
                  <Layout>
                    <CheckinDemoPage />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* Check-in routes */}
              <Route path="/events/:id/checkin" element={
                <ProtectedRoute>
                  <Layout>
                    <CheckInPage />
                  </Layout>
                </ProtectedRoute>
              } />

              <Route path="/events/:id/qr-scanner" element={
                <ProtectedRoute>
                  <Layout>
                    <QRScannerPage />
                  </Layout>
                </ProtectedRoute>
              } />

              {/* VIP Board - Public route (no auth) */}
              <Route path="/vip-board/:uuid" element={<VIPBoardPage />} />


              {/* Fallback route */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
          </Router>
        </AuthProvider>
      </QueryClientProvider>
    </ChakraProvider>
  );
}

export default App;